import { useMemo, useState } from 'react';
import {
  ComposedChart,
  Line,
  Bar,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { clsx } from 'clsx';
import { ChevronDown, TrendingUp, TrendingDown } from 'lucide-react';

// Available metrics for comparison
export type MetricKey =
  | 'stock_price'
  | 'fcf_per_share'
  | 'eps'
  | 'revenue'
  | 'net_income'
  | 'free_cash_flow'
  | 'operating_cashflow'
  | 'gross_profit'
  | 'shares_outstanding'
  | 'buybacks'
  | 'sbc';

export interface MetricConfig {
  key: MetricKey;
  label: string;
  color: string;
  format: 'currency' | 'currencySmall' | 'billions' | 'percent';
  description: string;
}

export const AVAILABLE_METRICS: MetricConfig[] = [
  { key: 'stock_price', label: 'Stock Price', color: '#3b82f6', format: 'currency', description: 'Historical stock price' },
  { key: 'fcf_per_share', label: 'FCF/Share', color: '#10b981', format: 'currencySmall', description: 'Free cash flow per share' },
  { key: 'eps', label: 'EPS', color: '#f59e0b', format: 'currencySmall', description: 'Earnings per share' },
  { key: 'revenue', label: 'Revenue', color: '#8b5cf6', format: 'billions', description: 'Total revenue' },
  { key: 'net_income', label: 'Net Income', color: '#ec4899', format: 'billions', description: 'Net income' },
  { key: 'free_cash_flow', label: 'Free Cash Flow', color: '#06b6d4', format: 'billions', description: 'Free cash flow' },
  { key: 'operating_cashflow', label: 'Operating Cash Flow', color: '#84cc16', format: 'billions', description: 'Cash from operations' },
  { key: 'gross_profit', label: 'Gross Profit', color: '#f97316', format: 'billions', description: 'Gross profit' },
  { key: 'shares_outstanding', label: 'Shares Outstanding', color: '#64748b', format: 'billions', description: 'Total shares outstanding' },
  { key: 'buybacks', label: 'Stock Buybacks', color: '#22c55e', format: 'billions', description: 'Stock repurchases' },
  { key: 'sbc', label: 'Stock-Based Comp', color: '#ef4444', format: 'billions', description: 'Stock-based compensation' },
];

// Format helpers
const formatValue = (value: number | null | undefined, format: string): string => {
  if (value === null || value === undefined) return '-';
  switch (format) {
    case 'currency':
      return `$${value.toFixed(2)}`;
    case 'currencySmall':
      return `$${value.toFixed(2)}`;
    case 'billions':
      if (Math.abs(value) >= 1e12) return `$${(value / 1e12).toFixed(1)}T`;
      if (Math.abs(value) >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
      if (Math.abs(value) >= 1e6) return `$${(value / 1e6).toFixed(0)}M`;
      return `$${value.toFixed(0)}`;
    case 'percent':
      return `${(value * 100).toFixed(1)}%`;
    default:
      return value.toFixed(2);
  }
};

const formatAxisValue = (value: number | null | undefined, format: string): string => {
  if (value === null || value === undefined) return '';
  switch (format) {
    case 'currency':
      return `$${value.toFixed(0)}`;
    case 'currencySmall':
      return `$${value.toFixed(2)}`;
    case 'billions':
      if (Math.abs(value) >= 1e12) return `${(value / 1e12).toFixed(1)}T`;
      if (Math.abs(value) >= 1e9) return `${(value / 1e9).toFixed(0)}B`;
      if (Math.abs(value) >= 1e6) return `${(value / 1e6).toFixed(0)}M`;
      return `${value.toFixed(0)}`;
    default:
      return value.toFixed(0);
  }
};

// Metric selector dropdown
interface MetricSelectorProps {
  value: MetricKey;
  onChange: (metric: MetricKey) => void;
  excludeMetrics?: MetricKey[];
  label: string;
}

function MetricSelector({ value, onChange, excludeMetrics = [], label }: MetricSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const selectedMetric = AVAILABLE_METRICS.find(m => m.key === value);
  const availableMetrics = AVAILABLE_METRICS.filter(m => !excludeMetrics.includes(m.key));

  return (
    <div className="relative">
      <label className="block text-xs text-[var(--color-text-muted)] mb-1">{label}</label>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={clsx(
          'flex items-center justify-between gap-2 w-full px-3 py-2 rounded-lg',
          'bg-[var(--color-bg-secondary)] border border-[var(--color-border)]',
          'text-sm text-[var(--color-text-primary)]',
          'hover:border-[var(--color-accent)] transition-colors'
        )}
      >
        <div className="flex items-center gap-2">
          <div
            className="w-3 h-3 rounded-full"
            style={{ backgroundColor: selectedMetric?.color }}
          />
          <span>{selectedMetric?.label}</span>
        </div>
        <ChevronDown className={clsx('h-4 w-4 transition-transform', isOpen && 'rotate-180')} />
      </button>

      {isOpen && (
        <div className={clsx(
          'absolute z-50 mt-1 w-full rounded-lg shadow-lg',
          'bg-[var(--color-bg-primary)] border border-[var(--color-border)]',
          'max-h-64 overflow-auto'
        )}>
          {availableMetrics.map(metric => (
            <button
              key={metric.key}
              onClick={() => {
                onChange(metric.key);
                setIsOpen(false);
              }}
              className={clsx(
                'flex items-center gap-2 w-full px-3 py-2 text-left text-sm',
                'hover:bg-[var(--color-bg-secondary)] transition-colors',
                metric.key === value && 'bg-[var(--color-bg-secondary)]'
              )}
            >
              <div
                className="w-3 h-3 rounded-full flex-shrink-0"
                style={{ backgroundColor: metric.color }}
              />
              <div>
                <div className="text-[var(--color-text-primary)]">{metric.label}</div>
                <div className="text-xs text-[var(--color-text-muted)]">{metric.description}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Data point structure
export interface ComparisonDataPoint {
  period: string; // "2023" for annual, "Q1 2023" for quarterly
  fiscal_date: string;
  stock_price?: number;
  fcf_per_share?: number;
  eps?: number;
  revenue?: number;
  net_income?: number;
  free_cash_flow?: number;
  operating_cashflow?: number;
  gross_profit?: number;
  shares_outstanding?: number;
  buybacks?: number;
  sbc?: number;
}

interface WeeklyPricePoint {
  date: string;
  close: number;
}

interface ComparisonChartProps {
  annualData: ComparisonDataPoint[];
  quarterlyData: ComparisonDataPoint[];
  weeklyPrices?: WeeklyPricePoint[];
  className?: string;
  title?: string;
  ticker?: string;
  defaultPrimaryMetric?: MetricKey;
  defaultSecondaryMetric?: MetricKey;
}

export function ComparisonChart({
  annualData,
  quarterlyData,
  weeklyPrices = [],
  className,
  title = 'Custom Comparison',
  ticker,
  defaultPrimaryMetric = 'stock_price',
  defaultSecondaryMetric = 'fcf_per_share',
}: ComparisonChartProps) {
  const [primaryMetric, setPrimaryMetric] = useState<MetricKey>(defaultPrimaryMetric);
  const [secondaryMetric, setSecondaryMetric] = useState<MetricKey>(defaultSecondaryMetric);
  const [viewMode, setViewMode] = useState<'annual' | 'quarterly'>('annual');

  const primaryConfig = AVAILABLE_METRICS.find(m => m.key === primaryMetric)!;
  const secondaryConfig = AVAILABLE_METRICS.find(m => m.key === secondaryMetric)!;

  // Check if stock_price is being used
  const usesStockPrice = primaryMetric === 'stock_price' || secondaryMetric === 'stock_price';
  const fundamentalMetric = primaryMetric === 'stock_price' ? secondaryMetric : primaryMetric;

  // Select data based on view mode
  const rawData = viewMode === 'annual' ? annualData : quarterlyData;

  // Whether we're in unified timeline mode (continuous price line + bars at fiscal dates)
  const useUnifiedTimeline = usesStockPrice && weeklyPrices.length > 0;

  // Build chart data - use fundamental periods as X-axis, map stock prices to those periods
  const chartData = useMemo(() => {
    if (!rawData || rawData.length === 0) return [];

    // Get fundamental data sorted by date
    const sortedFundamentals = [...rawData]
      .filter(point => point[fundamentalMetric] !== undefined && point[fundamentalMetric] !== null)
      .sort((a, b) => new Date(a.fiscal_date).getTime() - new Date(b.fiscal_date).getTime())
      .slice(-12);

    if (sortedFundamentals.length === 0) return [];

    // Unified timeline: all weekly price points + fundamental data merged at fiscal dates
    if (useUnifiedTimeline) {
      const sortedPrices = [...weeklyPrices].sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
      );

      // Determine the date range from fundamentals
      const firstFundDate = new Date(sortedFundamentals[0].fiscal_date).getTime();
      const lastFundDate = new Date(sortedFundamentals[sortedFundamentals.length - 1].fiscal_date).getTime();
      // Add some padding (90 days before first, 30 days after last)
      const rangeStart = firstFundDate - 90 * 24 * 60 * 60 * 1000;
      const rangeEnd = lastFundDate + 30 * 24 * 60 * 60 * 1000;

      // Filter prices to the fundamental date range
      const pricesInRange = sortedPrices.filter(p => {
        const t = new Date(p.date).getTime();
        return t >= rangeStart && t <= rangeEnd;
      });

      // Create a map of fundamental data by approximate date (within 7 days)
      const fundMap = new Map<number, typeof sortedFundamentals[0]>();
      for (const fund of sortedFundamentals) {
        fundMap.set(new Date(fund.fiscal_date).getTime(), fund);
      }

      // Build unified data points: every weekly price point
      const formatDate = (dateStr: string) => {
        const d = new Date(dateStr);
        return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      };

      const unifiedData: (ComparisonDataPoint & { displayDate: string })[] = [];
      const usedFundDates = new Set<number>();

      for (const pricePoint of pricesInRange) {
        const priceTime = new Date(pricePoint.date).getTime();

        // Check if any fundamental data point is within 15 days of this price point
        let matchedFund: typeof sortedFundamentals[0] | undefined;
        for (const [fundTime, fund] of fundMap.entries()) {
          if (Math.abs(priceTime - fundTime) < 15 * 24 * 60 * 60 * 1000 && !usedFundDates.has(fundTime)) {
            matchedFund = fund;
            usedFundDates.add(fundTime);
            break;
          }
        }

        unifiedData.push({
          period: matchedFund?.period ?? formatDate(pricePoint.date),
          fiscal_date: pricePoint.date,
          displayDate: formatDate(pricePoint.date),
          stock_price: pricePoint.close,
          // Only include fundamental metric values where there's a match
          ...(matchedFund ? {
            [fundamentalMetric]: matchedFund[fundamentalMetric],
            fcf_per_share: matchedFund.fcf_per_share,
            eps: matchedFund.eps,
            revenue: matchedFund.revenue,
            net_income: matchedFund.net_income,
            free_cash_flow: matchedFund.free_cash_flow,
            operating_cashflow: matchedFund.operating_cashflow,
            gross_profit: matchedFund.gross_profit,
            shares_outstanding: matchedFund.shares_outstanding,
            buybacks: matchedFund.buybacks,
            sbc: matchedFund.sbc,
          } : {}),
        });
      }

      return unifiedData;
    }

    // No stock price selected - use simple period-based approach
    return sortedFundamentals;
  }, [rawData, fundamentalMetric, useUnifiedTimeline, weeklyPrices]);

  // Calculate changes
  const analysis = useMemo(() => {
    if (chartData.length < 2) return null;

    const validPrimary = chartData.filter(d => d[primaryMetric] != null);
    const validSecondary = chartData.filter(d => d[secondaryMetric] != null);

    if (validPrimary.length < 2 && validSecondary.length < 2) return null;

    const firstPrimary = validPrimary[0]?.[primaryMetric] as number | undefined;
    const lastPrimary = validPrimary[validPrimary.length - 1]?.[primaryMetric] as number | undefined;
    const firstSecondary = validSecondary[0]?.[secondaryMetric] as number | undefined;
    const lastSecondary = validSecondary[validSecondary.length - 1]?.[secondaryMetric] as number | undefined;

    const primaryChange = firstPrimary && lastPrimary && firstPrimary !== 0
      ? ((lastPrimary - firstPrimary) / Math.abs(firstPrimary)) * 100
      : null;
    const secondaryChange = firstSecondary && lastSecondary && firstSecondary !== 0
      ? ((lastSecondary - firstSecondary) / Math.abs(firstSecondary)) * 100
      : null;

    return { primaryChange, secondaryChange, lastPrimary, lastSecondary };
  }, [chartData, primaryMetric, secondaryMetric]);

  // Calculate Y-axis domains to properly handle negative values
  const axisDomains = useMemo(() => {
    if (chartData.length === 0) return { left: [0, 'auto'], right: [0, 'auto'] };

    const primaryValues = chartData
      .map(d => d[primaryMetric] as number)
      .filter(v => v != null && !isNaN(v));
    const secondaryValues = chartData
      .map(d => d[secondaryMetric] as number)
      .filter(v => v != null && !isNaN(v));

    const getAxisDomain = (values: number[]): [number | 'auto', number | 'auto'] => {
      if (values.length === 0) return [0, 'auto'];
      const min = Math.min(...values);
      const max = Math.max(...values);

      // If all values are positive, start from 0
      if (min >= 0) return [0, 'auto'];

      // If there are negative values, add padding and include 0
      const padding = Math.abs(max - min) * 0.1;
      return [min - padding, max + padding];
    };

    return {
      left: getAxisDomain(primaryValues),
      right: getAxisDomain(secondaryValues),
      hasNegativeLeft: primaryValues.some(v => v < 0),
      hasNegativeRight: secondaryValues.some(v => v < 0),
    };
  }, [chartData, primaryMetric, secondaryMetric]);

  if ((!annualData || annualData.length === 0) && (!quarterlyData || quarterlyData.length === 0)) {
    return (
      <div className={clsx(
        'flex items-center justify-center h-64 rounded-lg',
        'bg-[var(--color-bg-secondary)] text-[var(--color-text-muted)]',
        className
      )}>
        No data available for comparison
      </div>
    );
  }

  const hasAnnualData = annualData && annualData.length > 0;
  const hasQuarterlyData = quarterlyData && quarterlyData.length > 0;

  return (
    <div className={clsx('w-full', className)}>
      {/* Header with controls */}
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-4">
        <h3 className="text-lg font-semibold text-[var(--color-text-primary)]">
          {ticker && <span className="text-[var(--color-text-muted)]">{ticker}</span>}{ticker && ' — '}{title}
        </h3>

        <div className="flex flex-wrap items-end gap-3">
          {/* Period toggle */}
          <div>
            <label className="block text-xs text-[var(--color-text-muted)] mb-1">Period</label>
            <div className="flex rounded-lg overflow-hidden border border-[var(--color-border)]">
              <button
                onClick={() => setViewMode('annual')}
                disabled={!hasAnnualData}
                className={clsx(
                  'px-3 py-2 text-sm font-medium transition-colors',
                  viewMode === 'annual'
                    ? 'bg-[var(--color-accent)] text-white'
                    : 'bg-[var(--color-bg-secondary)] text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]',
                  !hasAnnualData && 'opacity-50 cursor-not-allowed'
                )}
              >
                Annual
              </button>
              <button
                onClick={() => setViewMode('quarterly')}
                disabled={!hasQuarterlyData}
                className={clsx(
                  'px-3 py-2 text-sm font-medium transition-colors',
                  viewMode === 'quarterly'
                    ? 'bg-[var(--color-accent)] text-white'
                    : 'bg-[var(--color-bg-secondary)] text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]',
                  !hasQuarterlyData && 'opacity-50 cursor-not-allowed'
                )}
              >
                Quarterly
              </button>
            </div>
          </div>

          {/* Metric selectors */}
          <MetricSelector
            value={primaryMetric}
            onChange={setPrimaryMetric}
            excludeMetrics={[secondaryMetric]}
            label="Left Axis"
          />
          <MetricSelector
            value={secondaryMetric}
            onChange={setSecondaryMetric}
            excludeMetrics={[primaryMetric]}
            label="Right Axis"
          />
        </div>
      </div>

      {/* Compact analysis summary */}
      {analysis && (
        <div className="flex flex-wrap gap-3 mb-3 text-xs">
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: primaryConfig.color }} />
            <span className="text-[var(--color-text-muted)]">{primaryConfig.label}:</span>
            <span className="font-medium">{analysis.lastPrimary != null ? formatValue(analysis.lastPrimary, primaryConfig.format) : '-'}</span>
            {analysis.primaryChange != null && (
              <span className={analysis.primaryChange >= 0 ? 'text-emerald-500' : 'text-red-500'}>
                ({analysis.primaryChange >= 0 ? '+' : ''}{analysis.primaryChange.toFixed(0)}%)
              </span>
            )}
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: secondaryConfig.color }} />
            <span className="text-[var(--color-text-muted)]">{secondaryConfig.label}:</span>
            <span className="font-medium">{analysis.lastSecondary != null ? formatValue(analysis.lastSecondary, secondaryConfig.format) : '-'}</span>
            {analysis.secondaryChange != null && (
              <span className={analysis.secondaryChange >= 0 ? 'text-emerald-500' : 'text-red-500'}>
                ({analysis.secondaryChange >= 0 ? '+' : ''}{analysis.secondaryChange.toFixed(0)}%)
              </span>
            )}
          </span>
        </div>
      )}

      {/* Chart */}
      {chartData.length > 0 ? (
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%" minHeight={1}>
            <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--color-border)" />
              <XAxis
                dataKey={useUnifiedTimeline ? 'displayDate' : 'period'}
                tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }}
                axisLine={{ stroke: 'var(--color-border)' }}
                tickLine={false}
                interval={useUnifiedTimeline ? Math.max(0, Math.floor(chartData.length / 12) - 1) : undefined}
              />

              {/* Left Y-axis for primary metric */}
              <YAxis
                yAxisId="left"
                tick={{ fill: primaryConfig.color, fontSize: 11 }}
                axisLine={{ stroke: primaryConfig.color }}
                tickLine={false}
                tickFormatter={(val) => formatAxisValue(val, primaryConfig.format)}
                width={60}
                domain={axisDomains.left as [number, number]}
              />

              {/* Right Y-axis for secondary metric */}
              <YAxis
                yAxisId="right"
                orientation="right"
                tick={{ fill: secondaryConfig.color, fontSize: 11 }}
                axisLine={{ stroke: secondaryConfig.color }}
                tickLine={false}
                tickFormatter={(val) => formatAxisValue(val, secondaryConfig.format)}
                width={60}
                domain={axisDomains.right as [number, number]}
              />

              {/* Reference line at 0 for axes with negative values */}
              {axisDomains.hasNegativeLeft && (
                <ReferenceLine yAxisId="left" y={0} stroke="var(--color-border)" strokeDasharray="3 3" />
              )}
              {axisDomains.hasNegativeRight && (
                <ReferenceLine yAxisId="right" y={0} stroke="var(--color-border)" strokeDasharray="3 3" />
              )}

              <Tooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload || !payload.length) return null;
                  const dataPoint = payload[0]?.payload;
                  const tooltipLabel = useUnifiedTimeline ? (dataPoint?.displayDate ?? label) : label;
                  return (
                    <div className="bg-[var(--color-bg-primary)] border border-[var(--color-border)] rounded-lg p-3 shadow-lg">
                      <p className="text-sm font-medium text-[var(--color-text-primary)] mb-2">{tooltipLabel}</p>
                      {dataPoint[primaryMetric] != null && (
                        <div className="flex items-center gap-2 text-sm">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: primaryConfig.color }} />
                          <span className="text-[var(--color-text-muted)]">{primaryConfig.label}:</span>
                          <span className="font-medium text-[var(--color-text-primary)]">
                            {formatValue(dataPoint[primaryMetric], primaryConfig.format)}
                          </span>
                        </div>
                      )}
                      {dataPoint[secondaryMetric] != null && (
                        <div className="flex items-center gap-2 text-sm">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: secondaryConfig.color }} />
                          <span className="text-[var(--color-text-muted)]">{secondaryConfig.label}:</span>
                          <span className="font-medium text-[var(--color-text-primary)]">
                            {formatValue(dataPoint[secondaryMetric], secondaryConfig.format)}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                }}
              />
              <Legend wrapperStyle={{ fontSize: '11px' }} />

              {/* Primary metric - Bar for fundamentals, Line for stock price */}
              {primaryMetric === 'stock_price' ? (
                <Line
                  type="monotone"
                  dataKey={primaryMetric}
                  name={primaryConfig.label}
                  stroke={primaryConfig.color}
                  strokeWidth={2}
                  yAxisId="left"
                  dot={useUnifiedTimeline ? false : { fill: primaryConfig.color, r: 3 }}
                  connectNulls
                />
              ) : (
                <Bar
                  dataKey={primaryMetric}
                  name={primaryConfig.label}
                  fill={primaryConfig.color}
                  fillOpacity={0.8}
                  yAxisId="left"
                  radius={[4, 4, 0, 0]}
                  barSize={viewMode === 'annual' ? 40 : 16}
                />
              )}

              {/* Secondary metric - Line for stock price, Bar otherwise */}
              {secondaryMetric === 'stock_price' ? (
                <Line
                  type="monotone"
                  dataKey={secondaryMetric}
                  name={secondaryConfig.label}
                  stroke={secondaryConfig.color}
                  strokeWidth={2}
                  yAxisId="right"
                  dot={useUnifiedTimeline ? false : { fill: secondaryConfig.color, r: 4 }}
                  connectNulls
                />
              ) : (
                <Bar
                  dataKey={secondaryMetric}
                  name={secondaryConfig.label}
                  fill={secondaryConfig.color}
                  fillOpacity={0.8}
                  yAxisId="right"
                  radius={[4, 4, 0, 0]}
                  barSize={viewMode === 'annual' ? 40 : 16}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="h-64 flex items-center justify-center text-[var(--color-text-muted)]">
          No data available for selected metrics
        </div>
      )}

      {/* Help text */}
      <p className="text-xs text-[var(--color-text-muted)] text-center mt-2">
        <span className="text-[var(--color-text-secondary)]">Tip:</span> Look for divergences where fundamentals improve but price drops.
      </p>
    </div>
  );
}

// Time range options for stock price chart
type TimeRange = '1M' | '3M' | '6M' | '1Y' | '5Y' | 'ALL';

const TIME_RANGES: { key: TimeRange; label: string; days: number }[] = [
  { key: '1M', label: '1M', days: 30 },
  { key: '3M', label: '3M', days: 90 },
  { key: '6M', label: '6M', days: 180 },
  { key: '1Y', label: '1Y', days: 365 },
  { key: '5Y', label: '5Y', days: 1825 },
  { key: 'ALL', label: 'All', days: 99999 },
];

// Stock price chart component with time range filtering
interface PricePoint {
  date: string;
  close: number;
  open?: number;
  high?: number;
  low?: number;
  volume?: number;
}

interface StockPriceChartProps {
  data: PricePoint[];  // Weekly data for longer views
  dailyData?: PricePoint[];  // Daily data for 1M/3M views
  className?: string;
  ticker?: string;
}

export function StockPriceChart({ data, dailyData, className, ticker = 'Stock' }: StockPriceChartProps) {
  const [timeRange, setTimeRange] = useState<TimeRange>('1Y');

  // Select data source based on time range - use daily for short ranges if available
  const sourceData = useMemo(() => {
    // Use daily data for 1M and 3M if available
    if ((timeRange === '1M' || timeRange === '3M') && dailyData && dailyData.length > 0) {
      return dailyData;
    }
    // Use weekly data for longer ranges or if daily not available
    return data;
  }, [data, dailyData, timeRange]);

  // Filter and format data based on time range
  const chartData = useMemo(() => {
    if (!sourceData || sourceData.length === 0) return [];

    const now = new Date();
    const selectedRange = TIME_RANGES.find(r => r.key === timeRange) || TIME_RANGES[3];
    const cutoffDate = new Date(now.getTime() - selectedRange.days * 24 * 60 * 60 * 1000);

    const filtered = [...sourceData]
      .filter(point => {
        if (point.close === undefined || point.close === null) return false;
        const pointDate = new Date(point.date);
        return pointDate >= cutoffDate;
      })
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    // Determine date format based on actual data span
    const firstDate = new Date(filtered[0]?.date);
    const lastDate = new Date(filtered[filtered.length - 1]?.date);
    const daySpan = (lastDate.getTime() - firstDate.getTime()) / (1000 * 60 * 60 * 24);

    return filtered.map(point => {
      const date = new Date(point.date);
      let formattedDate: string;

      if (daySpan <= 45) {
        // Less than ~1.5 months: show "Jan 15"
        formattedDate = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      } else if (daySpan <= 120) {
        // ~1.5 to 4 months: show "Jan 15"
        formattedDate = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      } else if (daySpan <= 400) {
        // ~4 months to ~1 year: show "Jan '26"
        formattedDate = date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      } else if (daySpan <= 2000) {
        // ~1 to 5 years: show "Jan '24"
        formattedDate = date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      } else {
        // More than 5 years: show "2024"
        formattedDate = date.getFullYear().toString();
      }

      return {
        ...point,
        displayDate: formattedDate,
        rawDate: point.date,
      };
    });
  }, [sourceData, timeRange]);

  // Calculate stats for selected range
  const stats = useMemo(() => {
    const validData = chartData.filter(d => d.close !== undefined && d.close !== null && !isNaN(d.close));
    if (validData.length < 2) return null;

    const first = validData[0].close;
    const last = validData[validData.length - 1].close;

    if (first === undefined || first === null || last === undefined || last === null || first === 0) {
      return null;
    }

    const change = ((last - first) / first) * 100;
    const high = Math.max(...validData.map(d => d.close));
    const low = Math.min(...validData.map(d => d.close));
    return { first, last, change, high, low };
  }, [chartData]);

  if (!data || data.length === 0) {
    return (
      <div className={clsx(
        'flex items-center justify-center h-48 rounded-lg',
        'bg-[var(--color-bg-secondary)] text-[var(--color-text-muted)]',
        className
      )}>
        No price data available
      </div>
    );
  }

  const isPositive = stats && stats.change >= 0;

  return (
    <div className={clsx('w-full', className)}>
      {/* Header with title, stats, and time range buttons */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-3">
          <h3 className="text-base font-semibold text-[var(--color-text-primary)]">{ticker} Price</h3>
          {stats && (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-[var(--color-text-primary)] font-medium">
                ${stats.last.toFixed(2)}
              </span>
              <span className={clsx('flex items-center gap-0.5', isPositive ? 'text-emerald-500' : 'text-red-500')}>
                {isPositive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                {isPositive ? '+' : ''}{stats.change.toFixed(1)}%
              </span>
            </div>
          )}
        </div>

        {/* Time range buttons */}
        <div className="flex items-center gap-1">
          {TIME_RANGES.map(range => (
            <button
              key={range.key}
              onClick={() => setTimeRange(range.key)}
              className={clsx(
                'px-2 py-1 text-xs font-medium rounded transition-colors',
                timeRange === range.key
                  ? 'bg-[var(--color-accent)] text-white'
                  : 'bg-[var(--color-bg-secondary)] text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]'
              )}
            >
              {range.label}
            </button>
          ))}
        </div>
      </div>

      {/* Stats row */}
      {stats && (
        <div className="flex items-center gap-4 mb-2 text-xs text-[var(--color-text-muted)]">
          <span>High: <span className="text-[var(--color-text-secondary)]">${stats.high.toFixed(2)}</span></span>
          <span>Low: <span className="text-[var(--color-text-secondary)]">${stats.low.toFixed(2)}</span></span>
          <span>Points: <span className="text-[var(--color-text-secondary)]">{chartData.length}</span></span>
        </div>
      )}

      {/* Chart */}
      {chartData.length > 0 ? (
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%" minHeight={1}>
            <ComposedChart data={chartData} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--color-border)" />
              <XAxis
                dataKey="displayDate"
                tick={{ fill: 'var(--color-text-muted)', fontSize: 10 }}
                axisLine={{ stroke: 'var(--color-border)' }}
                tickLine={false}
                interval={Math.max(0, Math.floor(chartData.length / 10) - 1)}
              />
              <YAxis
                tick={{ fill: 'var(--color-text-muted)', fontSize: 10 }}
                axisLine={{ stroke: 'var(--color-border)' }}
                tickLine={false}
                tickFormatter={(val) => val != null ? `$${val.toFixed(0)}` : ''}
                domain={['auto', 'auto']}
                width={45}
              />
              <Tooltip
                content={({ active, payload }) => {
                  if (!active || !payload || !payload.length) return null;
                  const point = payload[0].payload;
                  return (
                    <div className="bg-[var(--color-bg-primary)] border border-[var(--color-border)] rounded-lg p-2 shadow-lg text-xs">
                      <p className="font-medium text-[var(--color-text-primary)]">
                        {new Date(point.rawDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </p>
                      <p className="text-[var(--color-text-muted)]">Close: ${point.close?.toFixed(2)}</p>
                      {point.volume && (
                        <p className="text-[var(--color-text-muted)]">Vol: {(point.volume / 1e6).toFixed(1)}M</p>
                      )}
                    </div>
                  );
                }}
              />
              <Area
                type="monotone"
                dataKey="close"
                stroke={isPositive ? '#10b981' : '#ef4444'}
                fill={isPositive ? '#10b981' : '#ef4444'}
                fillOpacity={0.1}
                strokeWidth={1.5}
                dot={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="h-48 flex items-center justify-center text-[var(--color-text-muted)] text-sm">
          No data for selected time range
        </div>
      )}
    </div>
  );
}
